// CORE
import React, { useState, useMemo } from 'react';
import type { MouseEventHandler } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener, { type ClickAwayListenerProps } from '@mui/material/ClickAwayListener';
import Popper, { type PopperPlacementType, type PopperProps } from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { boxShadowsToDropShadows } from 'themes/createTheme';
import { makeStyles } from 'tss-react/mui';

type PopperModifiers = NonNullable<PopperProps['modifiers']>;

export type GuidedTooltipProps = Omit<PopperProps, 'children' | 'modifiers' | 'open' | 'placement' | 'title'> & {
  open?: boolean;
  placement?: PopperPlacementType;
  /** Added after the default flip and arrow modifiers. */
  modifiers?: PopperModifiers;
  title?: string;
  body?: string;
  cancelLabel?: string;
  continueLabel?: string;
  onCancel?: MouseEventHandler<HTMLButtonElement>;
  onContinue?: MouseEventHandler<HTMLButtonElement>;
  /** Without it, clicks outside the tooltip are ignored. */
  onClickAway?: ClickAwayListenerProps['onClickAway'];
  noArrow?: boolean;
  /** Centers the arrow for `-start` and `-end` placements. */
  centerArrow?: boolean;
};

// A stable default, so the popper isn't rebuilt on every render.
const NO_MODIFIERS: PopperModifiers = [];

const arrowSize = 12;
const useStyles = makeStyles()((theme) => ({
  popper: {
    zIndex: theme.zIndex.tooltip + 1,
    filter: boxShadowsToDropShadows(theme.shadows[16]),
    background: `linear-gradient(145deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
    color: theme.palette.text.contrastText,
    borderRadius: theme.shape.borderRadius * 4,
    width: 450,
    padding: theme.spacing(2),
    '&[data-popper-placement*="left"] .arrow': {
      right: 0,
      marginRight: -2 * arrowSize,
      marginTop: theme.shape.borderRadius,
      marginBottom: theme.shape.borderRadius,
      borderTopColor: 'transparent',
      borderRightColor: 'transparent',
      borderBottomColor: 'transparent',
    },
    '&[data-popper-placement*="top"] .arrow': {
      bottom: 0,
      marginBottom: -2 * arrowSize + 1,
      marginLeft: theme.shape.borderRadius,
      marginRight: theme.shape.borderRadius,
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent',
      borderBottomColor: 'transparent',
    },
    '&[data-popper-placement*="right"] .arrow': {
      left: 0,
      marginLeft: -2 * arrowSize,
      marginTop: theme.shape.borderRadius,
      marginBottom: theme.shape.borderRadius,
      borderLeftColor: 'transparent',
      borderTopColor: 'transparent',
      borderBottomColor: 'transparent',
    },
    '&[data-popper-placement*="bottom"] .arrow': {
      top: 0,
      marginTop: -2 * arrowSize + 1,
      marginLeft: theme.shape.borderRadius,
      marginRight: theme.shape.borderRadius,
      borderLeftColor: 'transparent',
      borderTopColor: 'transparent',
      borderRightColor: 'transparent',
    },
  },
  arrow: {
    zIndex: theme.zIndex.tooltip,
    position: 'absolute',
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
    borderWidth: arrowSize,
  },
}));

type ArrowPaddingInput = { popper: { width: number; height: number } };

const centerArrowHorizontally = ({ popper }: ArrowPaddingInput) => popper.width / 2;
const centerArrowVertically = ({ popper }: ArrowPaddingInput) => popper.height / 2;

const getArrowPaddingFunction = (centerArrow: boolean, placement?: PopperPlacementType) => {
  if (centerArrow && placement) {
    if (placement.match(/(bottom|top)-(start|end)/)) {
      return centerArrowHorizontally;
    }

    if (placement.match(/(left|right)-(start|end)/)) {
      return centerArrowVertically;
    }
  }

  return undefined;
};

const GuidedTooltip = (props: GuidedTooltipProps) => {
  const {
    centerArrow = false,
    onClickAway,
    anchorEl = null,
    open = false,
    className,
    modifiers: ferryModifiers = NO_MODIFIERS,
    placement,
    title,
    body,
    cancelLabel,
    continueLabel,
    onCancel,
    onContinue,
    noArrow,
    style,
    ...otherProps
  } = props;
  const { classes, cx } = useStyles();

  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>();

  const content = useMemo(
    () => (
      <Box display='flex' flexDirection='column' alignItems='flex-start'>
        {!noArrow && <div className={cx(classes.arrow, 'arrow')} ref={(ref) => setArrowRef(ref)} />}
        <Typography variant='bodyMedium' color='inherit'>
          {title}
        </Typography>
        <Typography variant='paragraphRegular' color='inherit'>
          {body}
        </Typography>
        <Box width='100%' display='flex' alignItems='center' justifyContent='flex-end' mt={1}>
          {cancelLabel && (
            <Button onClick={onCancel} variant='text' color='inherit'>
              {cancelLabel}
            </Button>
          )}
          {continueLabel && (
            <Button variant='outlined' onClick={onContinue}>
              {continueLabel}
            </Button>
          )}
        </Box>
      </Box>
    ),
    [noArrow, body, cancelLabel, classes, continueLabel, onCancel, onContinue, title, cx],
  );

  const modifiers = useMemo(
    () => [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'right', 'bottom', 'left'],
        },
      },
      {
        name: 'arrow',
        options: {
          element: arrowRef,
          padding: getArrowPaddingFunction(centerArrow, placement),
        },
      },
      ...ferryModifiers,
    ],
    [ferryModifiers, centerArrow, placement, arrowRef],
  );

  return anchorEl && open ? (
    <Popper
      className={cx(classes.popper, className)}
      keepMounted={false}
      sharedcomponentid={'GUIDED_TOOLTIP'}
      placement={placement}
      open={open}
      anchorEl={anchorEl}
      modifiers={modifiers}
      style={{ margin: '8px', ...(style || {}) }}
      {...otherProps}
    >
      {onClickAway ? <ClickAwayListener onClickAway={onClickAway}>{content}</ClickAwayListener> : content}
    </Popper>
  ) : null;
};

export default GuidedTooltip;
